import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Sparkles, Check, Save, Square, CheckSquare, Zap } from 'lucide-react';
import { Input } from '@/components/ui/input';

const PageEditor = forwardRef(function PageEditor({ pageKey, label, defaults, generateArgs, selected, onToggleSelected, reloadKey }, ref) {
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({ seo_title: '', seo_description: '', keywords: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    base44.entities.SiteSeo.filter({ page_key: pageKey })
      .then((rows) => {
        if (!active) return;
        const r = rows?.[0];
        setRecord(r || null);
        setForm({ seo_title: r?.seo_title || '', seo_description: r?.seo_description || '', keywords: r?.keywords || '' });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [pageKey, reloadKey]);

  const gen = async () => {
    setLoading(true);
    try {
      const res = await base44.functions.invoke('generatePageSeo', generateArgs);
      const s = res.data?.suggestion;
      if (s) setForm({ seo_title: s.seo_title || '', seo_description: s.seo_description || '', keywords: (s.keywords || []).join(', ') });
    } catch (_e) {}
    setLoading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (record?.id) {
        await base44.entities.SiteSeo.update(record.id, form);
      } else {
        const created = await base44.entities.SiteSeo.create({ page_key: pageKey, ...form });
        setRecord(created);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      return true;
    } catch (_e) {
      return false;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save }), [save]);

  const isSel = selected.has(pageKey);

  return (
    <div className={`bg-white/[0.02] border p-5 ${isSel ? 'border-primary/50' : 'border-white/5'}`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => onToggleSelected(pageKey)} className="text-white/30 hover:text-white transition-colors">
            {isSel ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
          </button>
          <h3 className="text-white font-bold text-sm">{label}</h3>
        </div>
        <button onClick={gen} disabled={loading} className="bg-primary/10 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-[10px] tracking-widest px-3 py-2 flex items-center gap-2 transition-colors disabled:opacity-40">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} GENERATE WITH AI
        </button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1 block">SEO TITLE</label>
          <Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder={defaults?.title} />
        </div>
        <div>
          <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1 block">META DESCRIPTION</label>
          <textarea value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} rows={2} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 outline-none resize-none" placeholder={defaults?.description} />
        </div>
        <div>
          <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1 block">KEYWORD TAGS (comma separated)</label>
          <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="sim racing, racing simulator, ..." />
        </div>
        <button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest px-4 py-2.5 flex items-center gap-2 disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />} {saved ? 'SAVED' : 'SAVE SEO'}
        </button>
      </div>
    </div>
  );
});

export default function PageSeoPanel() {
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => base44.entities.Category.list('sort_order'),
  });

  const [selected, setSelected] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResult, setBulkResult] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const editorRefs = useRef({});
  const [savingSelected, setSavingSelected] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveResult, setSaveResult] = useState(null);

  const activeCats = categories.filter((c) => c.active);

  const homepagePage = {
    pageKey: 'home',
    label: 'HOMEPAGE',
    defaults: { title: 'Custom Sim Racing Rigs, Motion Platforms & Simulator Experiences', description: 'Motion Reality — bespoke racing simulators, pro-spec hardware and sim experiences in Bromborough, UK.' },
    generateArgs: { pageType: 'home' },
  };
  const experiencesPage = {
    pageKey: 'experiences',
    label: 'SIM EXPERIENCES (BOOKINGS)',
    defaults: { title: 'Book a Sim Racing Experience', description: 'Book a sim racing session at our Bromborough facility. Adult and junior sim racing experiences on pro-spec rigs. Gift vouchers available.' },
    generateArgs: { pageType: 'experiences' },
  };
  const competitionPage = {
    pageKey: 'competition',
    label: 'MONTHLY COMPETITION',
    defaults: { title: 'Win a Free Sim Racing Experience | Monthly Competition', description: 'Enter the Motion Reality monthly competition free. One random draw every month. Win a sim racing experience at our Bromborough venue. Open to UK residents aged 5+.' },
    generateArgs: { pageType: 'competition' },
  };
  const categoryPages = activeCats.map((c) => ({
    pageKey: `category:${c.key}`,
    label: c.label,
    generateArgs: { pageType: 'category', categoryKey: c.key, categoryLabel: c.label },
  }));
  const allPages = [homepagePage, experiencesPage, competitionPage, ...categoryPages];

  const toggleSelected = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const allSelected = allPages.length > 0 && selected.size === allPages.length;
  const toggleAll = () => setSelected((prev) => (prev.size === allPages.length ? new Set() : new Set(allPages.map((p) => p.pageKey))));

  const bulkGenerate = async () => {
    const keys = [...selected];
    if (keys.length === 0) return;
    setBulkRunning(true);
    setBulkResult(null);
    setBulkProgress(0);
    let done = 0;
    let errors = 0;
    for (const key of keys) {
      const page = allPages.find((p) => p.pageKey === key);
      if (!page) {
        errors++;
        continue;
      }
      try {
        const res = await base44.functions.invoke('generatePageSeo', page.generateArgs);
        const s = res.data?.suggestion;
        const payload = { seo_title: s?.seo_title || '', seo_description: s?.seo_description || '', keywords: (s?.keywords || []).join(', ') };
        const existing = await base44.entities.SiteSeo.filter({ page_key: key });
        if (existing?.[0]?.id) {
          await base44.entities.SiteSeo.update(existing[0].id, payload);
        } else {
          await base44.entities.SiteSeo.create({ page_key: key, ...payload });
        }
        done++;
      } catch (_e) {
        errors++;
      }
      setBulkProgress(Math.round(((done + errors) / keys.length) * 100));
    }
    setBulkResult({ done, errors, total: keys.length });
    setBulkRunning(false);
    setSelected(new Set());
    setReloadKey((k) => k + 1);
  };

  const saveSelected = async () => {
    const keys = [...selected];
    if (keys.length === 0) return;
    setSavingSelected(true);
    setSaveResult(null);
    setSaveProgress(0);
    let done = 0;
    let errors = 0;
    for (const key of keys) {
      const ok = await editorRefs.current[key]?.save?.();
      ok ? done++ : errors++;
      setSaveProgress(Math.round(((done + errors) / keys.length) * 100));
    }
    setSaveResult({ done, errors, total: keys.length });
    setSavingSelected(false);
    setReloadKey((k) => k + 1);
  };

  const setEditorRef = (key) => (el) => {
    if (el) editorRefs.current[key] = el;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1">PAGE SEO</p>
          <p className="text-white/60 text-sm">Generate and save AI-optimised SEO for the homepage and category pages. Select multiple to bulk-generate &amp; save.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={toggleAll} className="text-white/40 hover:text-white text-[10px] font-bold tracking-widest flex items-center gap-1.5">
            {allSelected ? <CheckSquare className="w-3.5 h-3.5 text-primary" /> : <Square className="w-3.5 h-3.5" />} {allSelected ? 'DESELECT ALL' : 'SELECT ALL'}
          </button>
          {selected.size > 0 && (
            <>
              <button
                onClick={bulkGenerate}
                disabled={bulkRunning || savingSelected}
                className="bg-primary/10 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-4 py-2.5 disabled:opacity-40 flex items-center gap-2"
              >
                {bulkRunning ? <><Loader2 className="w-4 h-4 animate-spin" /> {bulkProgress}%</> : <><Zap className="w-4 h-4" /> GENERATE ({selected.size})</>}
              </button>
              <button
                onClick={saveSelected}
                disabled={savingSelected || bulkRunning}
                className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-4 py-2.5 disabled:opacity-40 flex items-center gap-2"
              >
                {savingSelected ? <><Loader2 className="w-4 h-4 animate-spin" /> {saveProgress}%</> : <><Save className="w-4 h-4" /> SAVE ({selected.size})</>}
              </button>
            </>
          )}
        </div>
      </div>

      {bulkResult && (
        <div className={`px-4 py-3 text-xs border ${bulkResult.errors ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-300' : 'border-green-500/30 bg-green-500/10 text-green-300'}`}>
          Bulk generate complete — {bulkResult.done} pages updated{bulkResult.errors ? ` · ${bulkResult.errors} error(s)` : ''}.
        </div>
      )}
      {saveResult && (
        <div className={`px-4 py-3 text-xs border ${saveResult.errors ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-300' : 'border-green-500/30 bg-green-500/10 text-green-300'}`}>
          Saved {saveResult.done} page{saveResult.done !== 1 ? 's' : ''}{saveResult.errors ? ` · ${saveResult.errors} error(s)` : ''}.
        </div>
      )}

      <PageEditor
        ref={setEditorRef(homepagePage.pageKey)}
        pageKey={homepagePage.pageKey}
        label={homepagePage.label}
        defaults={homepagePage.defaults}
        generateArgs={homepagePage.generateArgs}
        selected={selected}
        onToggleSelected={toggleSelected}
        reloadKey={reloadKey}
      />
      <PageEditor
        ref={setEditorRef(experiencesPage.pageKey)}
        pageKey={experiencesPage.pageKey}
        label={experiencesPage.label}
        defaults={experiencesPage.defaults}
        generateArgs={experiencesPage.generateArgs}
        selected={selected}
        onToggleSelected={toggleSelected}
        reloadKey={reloadKey}
      />
      <PageEditor
        ref={setEditorRef(competitionPage.pageKey)}
        pageKey={competitionPage.pageKey}
        label={competitionPage.label}
        defaults={competitionPage.defaults}
        generateArgs={competitionPage.generateArgs}
        selected={selected}
        onToggleSelected={toggleSelected}
        reloadKey={reloadKey}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categoryPages.map((p) => (
          <PageEditor
            key={p.pageKey}
            ref={setEditorRef(p.pageKey)}
            pageKey={p.pageKey}
            label={p.label}
            generateArgs={p.generateArgs}
            selected={selected}
            onToggleSelected={toggleSelected}
            reloadKey={reloadKey}
          />
        ))}
      </div>
    </div>
  );
}